import React, { useState, useMemo, useContext } from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Activity, ChevronRight, Target } from 'lucide-react';
import { cn } from './SharedComponents';
import { 
  calculateVPPerformance, 
  SettingsContext,
  parseNumberSafe,
  MONTHS,
  getElapsedCompletedMonths
} from '../lib/shared-logic';

const COMISION_TYPE = 'COMISION';

const canonicalInstanceType = (type: any): 'COMISION' | 'COMITE' | 'CONSEJERIA' | string => {
  const normalized = String(type || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[\s_]/g, '')
    .trim()
    .toUpperCase();

  if (normalized === 'COMISION' || normalized === 'COMISIONESTRATEGICA' || normalized === 'COMISIONES') return 'COMISION';
  if (normalized === 'COMITE' || normalized === 'COMITES') return 'COMITE';
  if (normalized === 'CONSEJERIA' || normalized === 'CONSEJERIAS') return 'CONSEJERIA';
  return normalized;
};

const getVpAnalysisMonthLabel = (selectedMonth: string): string => {
  const targetIdx = MONTHS.indexOf(selectedMonth);
  if (targetIdx < 0) return selectedMonth;
  const analysisIdx = Math.max(0, getElapsedCompletedMonths(targetIdx));
  return MONTHS[analysisIdx] || selectedMonth;
};

const isFinitePercent = (value: number | null | undefined): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const buildVpPerformanceSettings = (instances: any[], settings: any) => {
  const counts = (instances || []).reduce((acc: Record<string, number>, inst: any) => {
    const type = canonicalInstanceType(inst?.type || inst?.commission_type);
    if (type === 'COMISION' || type === 'COMITE' || type === 'CONSEJERIA') {
      acc[type] = (acc[type] || 0) + 1;
    }
    return acc;
  }, { COMISION: 0, COMITE: 0, CONSEJERIA: 0 });

  return {
    ...settings,
    weight_comisiones: parseNumberSafe(settings?.weight_comisiones, 50) * Math.max(counts.COMISION, 1),
    weight_comites: parseNumberSafe(settings?.weight_comites, 30) * Math.max(counts.COMITE, 1),
    weight_consejerias: parseNumberSafe(settings?.weight_consejerias, 20) * Math.max(counts.CONSEJERIA, 1)
  };
};

export const NationalDashboardPanel = ({ hierarchy, settings: propsSettings, dashboardData, livePlanTotals, selectedMonth, onSelectVP }: any) => {
  const [selectedVPs, setSelectedVPs] = useState<string[]>([]);
  const contextSettings = useContext(SettingsContext);
  const settings = propsSettings || contextSettings;
  const vpData = hierarchy?.vicepresidencies || [];
  const analysisMonthLabel = getVpAnalysisMonthLabel(selectedMonth);

  const toggleVP = (id: string) => {
    if (id === 'ALL') {
      setSelectedVPs(selectedVPs.length === vpData.length ? [] : vpData.map((v: any) => v.id));
      return;
    }
    setSelectedVPs(prev => prev.includes(id) ? prev.filter(v => v !== id) : [...prev, id]);
  };

  const getVPPerformance = (vp: any) => {
    // REGLA v18.9.4: Unificación con lógica canónica de shared-logic
    const instances = [
      ...(vp.comisiones || []), 
      ...(vp.comites || []), 
      ...(vp.consejerias || [])
    ].map(inst => {
      const metrics = dashboardData?.find((d: any) => d.commission_id === inst.id);
      const livePlan = livePlanTotals?.find((p: any) => p.commission_id === inst.id);
      const isActiveVPInstance =
        inst?.active !== false &&
        inst?.is_active !== false &&
        inst?.status !== 'INACTIVO' &&
        inst?.status !== 'INACTIVE' &&
        inst?.status !== 'ARCHIVED';

      // REGLA DE ORO v18.9.78: Solo se marcan como activos si tienen metas reales en la DB
      // Esto previene el error del 0% al inflar el denominador con instancias vacías
      const planGoal = parseNumberSafe(livePlan?.total_goal ?? metrics?.plan_total_goal ?? metrics?.total_goal, 0);
      const planProgress = parseNumberSafe(livePlan?.total_progress ?? metrics?.plan_total_progress ?? metrics?.total_progress, 0);
      const planPct = planGoal > 0 ? Math.min(100, Math.round((planProgress / planGoal) * 100)) : 0;
      const kpiReal = parseNumberSafe(metrics?.total_real ?? metrics?.real_anual ?? metrics?.kpi_real ?? metrics?.kpi_progress, 0);
      const hasKpis = parseNumberSafe(metrics?.kpi_count || metrics?.total_indicators, 0) > 0
        && parseNumberSafe(metrics?.total_meta || metrics?.meta_anual, 0) > 0;
      const hasPlan = planGoal > 0;
      const kpiCompliance = hasKpis ? parseNumberSafe(metrics?.kpi_compliance, 0) : null;

      return {
        ...inst,
        // Normalización para calculateVPPerformance
        kpiCompliance,
        planProgress: planPct,
        kpiEligible: isActiveVPInstance && hasKpis,
        planEligible: isActiveVPInstance && hasPlan,
        hasActiveKpis: isActiveVPInstance && hasKpis,
        hasActivePlan: isActiveVPInstance && hasPlan
      };
    });

    if (instances.length === 0) {
      return {
        global: null,
        hasApplicableData: false
      };
    }
    
    // Delegamos el cálculo a la función centralizada
    return calculateVPPerformance(instances, buildVpPerformanceSettings(instances, settings));
  };

  const activeVPs = selectedVPs.length > 0 ? vpData.filter((v: any) => selectedVPs.includes(v.id)) : vpData;
  const activeVpSummaries = useMemo(
    () => activeVPs.map((vp: any) => ({ vp, performance: getVPPerformance(vp) })),
    [activeVPs, dashboardData, livePlanTotals, settings]
  );
  
  const integralScore = useMemo(() => {
    const applicableVPs = activeVpSummaries.filter(({ performance }) => isFinitePercent(performance?.global));
    if (applicableVPs.length === 0) return null;

    const totalSpecificWeight = applicableVPs.reduce((sum: number, { vp }: any) => sum + (vp.peso || 10), 0);
    if (totalSpecificWeight <= 0) return null;

    const weightedSum = applicableVPs.reduce((sum: number, { vp, performance }: any) => {
      const p = (vp.peso || 10) / totalSpecificWeight;
      return sum + ((performance.global as number) * p);
    }, 0);

    return Math.round(weightedSum);
  }, [activeVpSummaries]);

  const getSemaphoreColor = (val: number) => {
    if (val >= (settings?.threshold_green || 90)) return 'text-emerald-500';
    if (val >= (settings?.threshold_amber || 75)) return 'text-amber-500';
    return 'text-red-500';
  };

  const getSemaphoreBg = (val: number) => {
    if (val >= (settings?.threshold_green || 90)) return 'bg-emerald-500';
    if (val >= (settings?.threshold_amber || 75)) return 'bg-amber-500';
    return 'bg-red-500';
  };

  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.98 }} 
      animate={{ opacity: 1, scale: 1 }} 
      className="space-y-4 relative"
    >
      {/* CANARY YELLOW v18.9.5-HARD-STABLE - INDICADOR DE ESTABILIZACIÓN NACIONAL */}
      <div className="absolute -top-4 left-0 right-0 h-1 bg-yellow-500/50 z-50 pointer-events-none rounded-full" />
      
      {/* Badge de Versión Discreto */}
      <div className="flex justify-end mb-1">
        <span className="bg-slate-900/80 backdrop-blur-md border border-yellow-500/30 text-yellow-500 text-[8px] font-black px-3 py-1 rounded-full uppercase tracking-widest shadow-xl">
          National Dashboard
        </span>
      </div>

      <div className="bg-slate-950 border border-slate-800 p-4 rounded-2xl relative overflow-hidden group shadow-xl">
        <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/10 rounded-full -mr-32 -mt-32 blur-[80px]"></div>
        <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-indigo-700 p-0.5 shadow-lg">
               <div className="w-full h-full bg-slate-950 rounded-[0.7rem] flex items-center justify-center text-emerald-500">
                  <ShieldCheck size={16} strokeWidth={2} />
               </div>
            </div>
            <div>
              <p className="text-emerald-500 text-[9px] font-black uppercase tracking-[0.2em] mb-0.5 leading-none">Presidencia Nacional</p>
              <h1 className="text-xl font-black text-white tracking-tighter uppercase leading-none">Tablero Integral 2026</h1>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-4 bg-slate-900/40 p-2 px-5 rounded-xl border border-slate-800/50 backdrop-blur-md">
             <div className="grid grid-cols-2 gap-4 text-right">
                <div>
                   <p className="text-[8px] text-emerald-400 font-black uppercase tracking-widest">Periodo Analizado</p>
                   <p className="text-xs text-white font-black uppercase">{analysisMonthLabel} 2026</p>
                </div>
                <div>
                   <p className="text-[8px] text-slate-500 font-black uppercase tracking-widest">Foco Actual</p>
                   <p className="text-xs text-white font-black uppercase">{selectedMonth} 2026</p>
                </div>
             </div>
              <div className="text-right">
                 <h3 className={`text-4xl font-black tracking-tighter leading-none ${isFinitePercent(integralScore) ? getSemaphoreColor(integralScore) : 'text-slate-500'}`}>
                  {isFinitePercent(integralScore) ? `${integralScore}%` : 'N/A'}
                </h3>
             </div>
             <div className="flex gap-1.5">
                <div className={`w-2.5 h-2.5 rounded-full shadow-[0_0_8px] ${isFinitePercent(integralScore) && integralScore >= (settings?.threshold_green || 90) ? 'bg-emerald-500 shadow-emerald-500/50' : 'bg-slate-800'}`}></div>
                <div className={`w-2.5 h-2.5 rounded-full shadow-[0_0_8px] ${isFinitePercent(integralScore) && integralScore >= (settings?.threshold_amber || 75) && integralScore < (settings?.threshold_green || 90) ? 'bg-amber-500 shadow-amber-500/50' : 'bg-slate-800'}`}></div>
                <div className={`w-2.5 h-2.5 rounded-full shadow-[0_0_8px] ${isFinitePercent(integralScore) && integralScore < (settings?.threshold_amber || 75) ? 'bg-red-500 shadow-red-500/50' : 'bg-slate-800'}`}></div>
             </div>
          </div>
        </div>
      </div>

      <div className="bg-slate-900/40 border border-slate-800 p-2 rounded-xl flex items-center gap-2 overflow-x-auto scrollbar-hide">
         <button 
           onClick={() => toggleVP('ALL')}
           className={cn(
             "px-4 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all",
             selectedVPs.length === vpData.length || selectedVPs.length === 0 ? "bg-emerald-600 text-white" : "bg-slate-800 text-white/40 hover:text-white"
           )}
         >
           {selectedVPs.length === vpData.length || selectedVPs.length === 0 ? 'TODAS LAS VICEPRESIDENCIAS' : 'Seleccionar TODAS'}
         </button>
         <div className="w-px h-4 bg-slate-800 mx-2"></div>
         {vpData.map((v: any) => (
           <button 
             key={v.id}
             onClick={() => toggleVP(v.id)}
             className={cn(
               "px-3 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all whitespace-nowrap",
               selectedVPs.includes(v.id) ? "bg-emerald-600/20 text-emerald-400 border border-emerald-500/30" : "bg-slate-800/40 text-white/40 border border-transparent hover:border-slate-700"
             )}
           >
             {v.name}
           </button>
         ))}
      </div>

      <div className="space-y-3">
        {/* CABECERA DE LISTA OPERATIVA */}
        <div className="flex items-center px-8 py-4 bg-slate-950/40 border border-slate-800 rounded-2xl text-[10px] font-black uppercase tracking-[0.3em] text-white/30 hidden md:flex">
          <div className="w-[50%]">Vicepresidencia Estratégica</div>
          <div className="w-[20%] text-center">Organismos</div>
          <div className="w-[20%] text-center">Cumplimiento Integral</div>
          <div className="w-[10%] text-right">Acción</div>
        </div>

        {activeVpSummaries.map(({ vp: v, performance }: any) => {
          const score = isFinitePercent(performance?.global) ? performance.global : null;
          const instancesCount = (v.comisiones?.length || 0) + (v.comites?.length || 0) + (v.consejerias?.length || 0);
          
          return (
            <motion.div 
              key={v.id}
              whileHover={{ x: 4 }}
              onClick={() => onSelectVP && onSelectVP(v.id)}
              className="bg-slate-900/40 hover:bg-slate-900 border border-slate-800 p-5 rounded-2xl cursor-pointer group transition-all"
            >
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="w-full md:w-[50%] flex items-center gap-4">
                  <div className={cn(
                    "w-10 h-10 rounded-xl flex items-center justify-center shadow-lg",
                    score === null ? "bg-slate-700 text-slate-300" : `${getSemaphoreBg(score)} text-white`
                  )}>
                    <Activity size={18} />
                  </div>
                  <div className="min-w-0">
                    <h4 className="text-white font-black uppercase text-sm group-hover:text-emerald-400 transition-colors truncate">{v.name}</h4>
                    <p className="text-[8px] text-white/20 font-bold uppercase tracking-[0.2em] mt-1 line-clamp-1">Consolidado Nacional 2026</p>
                  </div>
                </div>

                <div className="w-full md:w-[20%] flex flex-col items-center justify-center">
                  <span className="text-[8px] font-black text-white/20 uppercase tracking-widest md:hidden mb-1">Organismos</span>
                  <span className="text-sm font-mono font-black text-white/60">{instancesCount} <span className="text-[9px] text-white/20 ml-1">INST</span></span>
                </div>

                <div className="w-full md:w-[20%] flex flex-col items-center justify-center gap-1">
                  <span className="text-[8px] font-black text-white/20 uppercase tracking-widest md:hidden mb-1">Cumplimiento</span>
                  <div className="flex items-center gap-3">
                    <span className={cn("text-xl font-black font-mono tracking-tighter", score === null ? 'text-slate-500' : getSemaphoreColor(score))}>{score === null ? 'N/A' : `${score}%`}</span>
                    <div className="w-12 h-1 bg-slate-950 rounded-full overflow-hidden">
                      <div className={cn("h-full", score === null ? 'bg-slate-700' : getSemaphoreBg(score))} style={{ width: `${score ?? 0}%` }}></div>
                    </div>
                  </div>
                </div>

                <div className="w-full md:w-[10%] flex justify-center md:justify-end">
                  <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center text-slate-600 group-hover:bg-emerald-500 group-hover:text-white transition-all">
                    <ChevronRight size={16} />
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
};

export default NationalDashboardPanel;
